import React, { useState } from "react";
import { Row, Col, Button } from "antd";
import { Link, useHistory } from "react-router-dom";
import styles from "./Payment.module.scss";
import commonStyles from "../DashBoard.module.scss";
import commonMobileStyles from "../DashBoardMobile/DashBoardMobile.module.scss";
import {
  backArrowIcon,
  circleRightArrow,
  dashBoardFilterIcon,
  filterIconDesktop,
} from "../../../assets/images";
import CustomButton from "../../../common/Buttons/CustomButton";
import moment, { MomentInput } from "moment";
import GoogleLoader from "../../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import { IExcelData } from "../../../types/TripTypes";
import { useWindowSize } from "../../../hooks/useWindowSize";
import getTransactionTypeIcon from "../../../helpers/getTransactionTypeIcon";
import BillingHistoryFilterModal from "./BillingHistoryFilterModal";
import xlsx from "xlsx-js-style";
import BillingHistoryFilterDrawer from "./BillingHistoryFilterDrawer";
import {
  ITransactionDetails,
  PaymentTransactionTypeIds,
  excelColumns,
} from "../../../types/PaymentTransactionTypes";
import BillingHistoryTable from "./BillingHistoryTable";
import { useBillingHistoryCtx } from "../../../context/BillingHistoryContext";
import BillingHistoryDateRangePicker from "./BillingHistoryDateRangePicker";
import { formatUTCtoLocalDate } from "../../../helpers/dateFormatters";

type Props = {
  dataSource: ITransactionDetails[];
  isLoading: boolean;
};

const BillingHistory: React.FC<Props> = ({ dataSource, isLoading }) => {
  const [isOpenFilter, setIsOpenFilter] = useState<boolean>(false);
  const [filterCount, setFilterCount] = useState<number>(0);
  const [openMobileFilter, setOpenMobileFilter] = useState<boolean>(false);

  const { dateRangeCtx, resetDefaults } = useBillingHistoryCtx();

  const [dateRange] = dateRangeCtx;

  const history = useHistory();
  const { width } = useWindowSize();

  const handleBack = () => {
    history.push("/DashBoard");
    resetDefaults();
  };

  const handleUpdatedDataForExcel = () => {
    let excelData: IExcelData[] = JSON.parse(JSON.stringify(dataSource));
    excelData.forEach((data: IExcelData) => {
      if (["Refund", "Credit"].includes(data.paymentTransactionTypeName)) {
        data.amount = "$(" + Number(data.amount).toFixed(2) + ")";
        data.serviceType = "";
      } else {
        data.amount = "$" + Number(data.amount).toFixed(2);
        if (data.paymentTransactionTypeName === "Subscription") {
          data.serviceType = data.subscriptionName;
        }
      }

      data.createdOn = moment(data?.createdOn).format("DD-MM-YYYY");
    });
    return excelData;
  };

  const handleDownloadCSV = () => {
    const excelColumnData = handleUpdatedDataForExcel();
    const header = excelColumns?.map((column) => column.title);
    const data = excelColumnData?.map((item: IExcelData) =>
      excelColumns.map((column) => item[column.key as keyof typeof item])
    );

    const ws = xlsx.utils.json_to_sheet([header, ...data], {
      skipHeader: true,
    });

    for (let row = 2; row <= data.length + 1; row++) {
      const cellAddress = `B${row}`;
      const cellAddressRef = `C${row}`;
      const cellValueRef = ws[cellAddressRef].v;
      if (["Refund", "Credit"].includes(cellValueRef)) {
        const cellStyle = {
          font: {
            color: { rgb: "FF0000" },
          },
        };
        ws[cellAddress].s = cellStyle;
      }
    }

    const fileName = `WandaBillingHistory${moment(dateRange.startDate).format(
      "MMDDYY"
    )}-${moment(dateRange.endDate).format("MMDDYY")}`;

    if (data.length > 0) {
      const colWidths = getColumnWidths(data);
      ws["!cols"] = colWidths.map((w) => ({ wch: w + 2 }));
    }
    const wb = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(wb, ws, "WandaBillingHistory");
    xlsx.writeFile(wb, fileName + ".xlsx");
  };

  function getColumnWidths(excelData: MomentInput[][]) {
    const colLengths = Object.keys(excelData[0]).map((k) => k.length);
    for (const d of excelData) {
      Object.values(d).forEach((element, index) => {
        const length = element ? element.toString().length : 0;
        if (colLengths[index] < length) {
          colLengths[index] = length;
        }
      });
    }
    return colLengths;
  }

  const handleFilter = () => setIsOpenFilter(true);

  const showDrawer = (): void => setOpenMobileFilter(true);

  const handleFilterCount = (count: number): void => setFilterCount(count);

  const DesktopTable: React.FC = () => {
    return (
      <Col xs={0} lg={24} id={commonStyles.tableWrapper}>
        <BillingHistoryTable dataSource={dataSource} />
      </Col>
    );
  };

  const MobileList: React.FC = () => {
    return (
      <Col xs={24} lg={0}>
        <Row className={commonMobileStyles.serviceListMargin}>
          <Col span={24}>
            {dataSource?.map((transaction) => (
              <Link
                to={`/DashBoard/BillingHistory/${transaction.paymentTransactionId}`}
                key={transaction.paymentTransactionId}
              >
                <Row align="middle" className={commonMobileStyles.tripRows}>
                  <Col span={4}>
                    <span className={commonMobileStyles.tripImageLarge}>
                      {getTransactionTypeIcon(
                        transaction.paymentTransactionTypeName
                      )}
                    </span>
                  </Col>
                  <Col span={16}>
                    <Row className={commonMobileStyles.boldText}>
                      <Col span={24}>
                        {transaction.paymentTransactionTypeId ===
                          PaymentTransactionTypeIds.Refund && "+"}
                        ${transaction.amount.toFixed(2)}
                      </Col>
                    </Row>
                    <Row className={commonMobileStyles.normalText}>
                      <Col span={24}>
                        <span>
                          {formatUTCtoLocalDate(
                            transaction.createdOn,
                            "MM/DD/YYYY"
                          )}
                           · 
                          {transaction.paymentTransactionTypeName}
                        </span>
                      </Col>
                      <Col span={24}>
                        <Row>
                          <Col span={24}>
                            {`${
                              transaction?.paymentInstrumentName.split("-")[0]
                            }(*${transaction?.paymentInstrumentName?.slice(
                              -4
                            )})`}
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Col>
                  <Col span={4}>
                    <Row justify="end">
                      <img src={circleRightArrow} alt="right arrow" />
                    </Row>
                  </Col>
                </Row>
              </Link>
            ))}
          </Col>
        </Row>
      </Col>
    );
  };

  const NoBillingHistoryMessage: React.FC = () => {
    return (
      <Col span={24}>
        {dataSource.length === 0 && (
          <Row justify="center" gutter={[0, 100]}>
            <Col span={24} />
            <Col className={styles.noBillingHistory}>
              <Row justify="center">
                <Col>There is no billing history&nbsp;</Col>
                <Col>for the selected date range.</Col>
              </Row>
            </Col>
          </Row>
        )}
      </Col>
    );
  };

  return (
    <>
      <Row>
        <Col span={2} />

        <Col span={20}>
          <Row>
            <Col lg={{ offset: 1, span: 23 }}>
              <CustomButton
                text="Back"
                handlerFunction={handleBack}
                type="link"
                icon={backArrowIcon}
                className="backBtn"
              />
            </Col>
          </Row>
          <Row className={styles.billingHistoryHeader}>
            <Col span={24}>
              <Row justify="space-between">
                <Col>Billing History</Col>
                <Col lg={0}>
                  <Button
                    className={
                      filterCount > 0
                        ? commonMobileStyles.filterBtnCount
                        : commonMobileStyles.filterBtn
                    }
                    onClick={showDrawer}
                  >
                    <div className={commonMobileStyles.filterBtnContent}>
                      {filterCount > 0 ? <span>{filterCount}</span> : ""}
                      <img src={dashBoardFilterIcon} alt="filter" />
                    </div>
                  </Button>
                </Col>
              </Row>
            </Col>
            <Col span={24}>
              <Row>
                <Col xs={24} lg={12}>
                  <Row justify="start" gutter={[15, 0]}>
                    <Col>
                      <CustomButton
                        text={width > 992 ? "Download CSV" : "Download"}
                        handlerFunction={handleDownloadCSV}
                        type="default"
                        className="downloadCSV"
                      />
                    </Col>
                    <Col>
                      <BillingHistoryDateRangePicker />
                    </Col>
                  </Row>
                </Col>
                <Col xs={0} lg={12}>
                  <Row justify="end">
                    <Col>
                      <CustomButton
                        text="Filters"
                        handlerFunction={handleFilter}
                        type="default"
                        className="desktopFilterBtn"
                        icon={filterIconDesktop}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            {isLoading ? (
              <GoogleLoader />
            ) : (
              <>
                <DesktopTable />
                <MobileList />
                <NoBillingHistoryMessage />
              </>
            )}
          </Row>
        </Col>

        <Col span={2} />

        <BillingHistoryFilterModal
          isOpenFilter={isOpenFilter}
          setIsOpenFilter={setIsOpenFilter}
          onFilterCount={handleFilterCount}
        />

        <BillingHistoryFilterDrawer
          isOpenFilter={openMobileFilter}
          setIsOpenFilter={setOpenMobileFilter}
          onFilterCount={handleFilterCount}
        />
      </Row>
    </>
  );
};

export default BillingHistory;
